import React, { useState, useEffect, useMemo } from "react";
import { FaEye, FaPen, FaTrash } from "react-icons/fa";
import { ChevronLeft, ChevronRight, ChevronDoubleLeft, ChevronDoubleRight } from "../ui/Icons";
import ActionButtons from "./ActionButtons";
import { API_BASE_URL } from "../../config";
import Table from "../ui/Table";
import Badge from "../ui/Badge";
import Button from "../ui/Button";

/* ── Page Button ─────────────────────────────────────── */
const PageButton = ({ children, isActive, disabled, onClick, iconOnly = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={[
      "inline-flex items-center justify-center rounded-lg border text-xs font-medium transition-colors duration-150",
      "focus:outline-none focus:ring-2 focus:ring-primary/30",
      "disabled:opacity-40 disabled:cursor-not-allowed",
      iconOnly ? "w-7 h-7" : "min-w-[28px] h-7 px-2",
      isActive
        ? "bg-primary text-white border-primary"
        : "bg-white text-ink-700 border-border hover:bg-canvas hover:text-ink-900",
    ].filter(Boolean).join(" ")}
  >
    {children}
  </button>
);

const TablePagination = ({
  tabledata,
  initialRowsPerPage,
  imagecolumns,
  showViewOption,
  showEditOption,
  showDeleteOption,
  onClickView,
  onClickEdit,
  onClickDel,
  actionButtons,
  visibleFields,
  updateList,
  roleName,
  entity,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [totalPages, setTotalPages] = useState(
    Math.ceil((tabledata || []).length / rowsPerPage)
  );

  const columns = useMemo(() => {
    if (!tabledata || tabledata.length === 0) return [];

    switch (entity) {
      case "accounts":
        return [
          "customerUniqueId",
          "transactionId",
          "transactionType",
          "accountType",
          "cash_type",
          "amount",
          "date",
        ];
      case "roles":
        return ["roleName"];
      // staffId is an internal sequence number and is not shown. It is still
      // fetched (EntityDetails.js:84) and still minted on save — only hidden
      // here. Row actions key off `row.id`, not staffId, so they are unaffected.
      //
      // ServerTablePagination.js:117 carries the same list. Change both.
      case "staff":
        return ["name", "designation", "branchName"];
      default:
        return Object.keys(tabledata[0]).filter(
          (col) => col !== "id" && (!visibleFields || visibleFields.includes(col))
        );
    }
  }, [tabledata, entity, visibleFields]);

  const imageColumnsExist = Array.isArray(imagecolumns) && imagecolumns.length > 0;

  useEffect(() => {
    const dataLength = (tabledata || []).length;
    const newRowsPerPage =
      initialRowsPerPage > dataLength && dataLength > 0
        ? dataLength
        : initialRowsPerPage;
    setRowsPerPage(newRowsPerPage);
    setTotalPages(Math.ceil(dataLength / newRowsPerPage));
    setCurrentPage(1);
  }, [tabledata, initialRowsPerPage]);

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setTotalPages(Math.ceil((tabledata || []).length / value));
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentPageData = (tabledata || []).slice(
    startIndex,
    startIndex + rowsPerPage
  );

  const getHeaderLabel = (column) => {
    switch (column) {
      case "customerUniqueId": return "CUSTOMER ID";
      case "transactionId": return "TRANSACTION ID";
      case "transactionType": return "TRANSACTION TYPE";
      case "accountType": return "ACCOUNT TYPE";
      case "cash_type": return "CASH TYPE";
      case "amount": return "AMOUNT";
      case "date": return "DATE";
      case "roleName": return "ROLE NAME";
      case "staffId": return "STAFF ID";
      case "name": return "NAME";
      // Without this the default below renders "BRANCHNAME".
      case "branchName": return "BRANCH";
      default: return column.toUpperCase();
    }
  };

  // Generate pagination page numbers
  const getPageNumbers = () => {
    const pages = [];
    const range = 2; // Show 2 pages before and after the current page

    pages.push(1);

    if (currentPage > range + 2) {
      pages.push("...");
    }

    const start = Math.max(2, currentPage - range);
    const end = Math.min(totalPages - 1, currentPage + range);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - range - 1) {
      pages.push("...");
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="flex-grow flex-1 min-h-0 flex flex-col overflow-hidden">
      <Table className="flex-grow flex-1 min-h-0">
        <Table.Head>
          <tr>
            <Table.HeadCell align="center" className="w-[5%] min-w-[50px] whitespace-nowrap">No</Table.HeadCell>
            {columns.map((column, index) => {
              const align = ["status", "actions", "transactionType"].includes(column) ? "center" : "left";
              let widthClass = "w-[15%] min-w-[110px]"; // default
              const name = column.toLowerCase();
              if (name === "name") {
                widthClass = "w-[15%] min-w-[120px]";
              } else if (name === "branchname" || name === "centername" || name === "rolename") {
                widthClass = "w-[30%] min-w-[160px]";
              } else if (name.includes("id") || name.includes("unique")) {
                widthClass = "w-[20%] min-w-[120px]";
              } else if (name === "status") {
                widthClass = "w-[10%] min-w-[90px]";
              }

              return (
                <Table.HeadCell
                  key={index}
                  align={align}
                  className={widthClass}
                >
                  {getHeaderLabel(column)}
                </Table.HeadCell>
              );
            })}
            {(showViewOption || showEditOption || showDeleteOption) && (
              <Table.HeadCell align="center" className="w-[12%] min-w-[150px] whitespace-nowrap">Actions</Table.HeadCell>
            )}
          </tr>
        </Table.Head>
        <Table.Body>
          {currentPageData.length > 0 ? (
            currentPageData.map((row, index) => (
              <Table.Row key={index}>
                <Table.Cell align="center" className="w-[5%] min-w-[50px] whitespace-nowrap font-semibold text-ink-500">
                  {index + 1 + startIndex}
                </Table.Cell>
                {columns.map((column, index1) => {
                  const align = ["status", "actions", "transactionType"].includes(column) ? "center" : "left";
                  return (
                    <Table.Cell
                      key={index1}
                      align={align}
                      className={[
                        column === "customerUniqueId" ? "font-semibold text-primary-600" : "",
                        column === "transactionId" ? "font-data" : "",
                      ].join(" ")}
                    >
                      {imageColumnsExist && imagecolumns.includes(column) && row[column] ? (
                        <div className="flex justify-center">
                          <img
                            src={`${API_BASE_URL}/images/${row[column]}`}
                            alt="img"
                            className="w-8 h-8 rounded object-cover border border-border-light shadow-sm"
                          />
                        </div>
                      ) : column === "date" && row[column] ? (
                        <span className="font-data">{row[column].split("-").reverse().join("-")}</span>
                      ) : column === "amount" && row[column] != null ? (
                        <span className="font-data font-semibold text-ink-900">
                          {Number(row[column]).toLocaleString("en-IN")}
                        </span>
                      ) : column === "transactionType" ? (
                        <Badge variant={String(row[column]).toLowerCase() === "deposit" || String(row[column]).toLowerCase() === "credit" ? "success" : "danger"} dot>
                          {row[column]}
                        </Badge>
                      ) : (
                        row[column] || "N/A"
                      )}
                    </Table.Cell>
                  );
                })}
                {(showViewOption || showEditOption || showDeleteOption) && (
                  <Table.Cell align="center" className="w-[12%] min-w-[150px] whitespace-nowrap">
                    <div className="flex items-center justify-center gap-2">
                      {showViewOption && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          onClick={() => onClickView(row.id)}
                          className="!text-info-600 bg-info-50 border border-info-200 hover:bg-info-100 hover:text-info-700"
                          title="View"
                        >
                          <FaEye className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {showEditOption && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          onClick={() => onClickEdit(row.id)}
                          className="!text-warning-600 bg-warning-50 border border-warning-200 hover:bg-warning-100 hover:text-warning-700"
                          title="Edit"
                        >
                          <FaPen className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {showDeleteOption && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          onClick={() => onClickDel(row.id)}
                          className="!text-danger-500 bg-danger-50 border border-danger-200 hover:bg-danger-100 hover:text-danger-700"
                          title="Delete"
                        >
                          <FaTrash className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {actionButtons && (
                        <ActionButtons item={row} updateList={updateList} />
                      )}
                    </div>
                  </Table.Cell>
                )}
              </Table.Row>
            ))
          ) : (
            <Table.EmptyRow colSpan={columns.length + 2} message="No records available" />
          )}
        </Table.Body>
      </Table>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-end gap-4 mt-2 text-xs font-semibold text-ink-500">
          {/* Rows per page selector (Commented out per user request) */}
          {/*
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              className="border border-border-light rounded-lg px-2.5 py-1 bg-white text-xs font-semibold text-ink-700 outline-none focus:ring-1 focus:ring-primary focus:border-primary shadow-sm"
            >
              {[5, 10, 25, 50, 100].map((option) => (
                <option key={option} value={option}>
                  {option}
                          {/* Page navigation buttons */}
          <div className="flex items-center gap-1">
            <PageButton iconOnly disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>
              <ChevronDoubleLeft />
            </PageButton>
            <PageButton iconOnly disabled={currentPage <= 1} onClick={() => setCurrentPage(currentPage - 1)}>
              <ChevronLeft />
            </PageButton>

            {getPageNumbers().map((page, idx) =>
              page === "..." ? (
                <span key={`ellipsis-${idx}`} className="inline-flex items-center justify-center w-7 h-7 text-xs text-ink-300">…</span>
              ) : (
                <PageButton
                  key={idx}
                  isActive={page === currentPage}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </PageButton>
              )
            )}

            <PageButton iconOnly disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
              <ChevronRight />
            </PageButton>
            <PageButton iconOnly disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>
              <ChevronDoubleRight />
            </PageButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default TablePagination;
